import * as React from 'react';
import * as SignalR from '@microsoft/signalr';
import { datadogLogs } from '@datadog/browser-logs';
import useAppBrowserLock from '../hooks/useAppBrowserLock';

type UseSignalROptions = {
  connectionName: string;
  url: string;
  contextConnection?: SignalR.HubConnection | undefined; // This parameter is used to make the connection unique for every hook usage
  autoConnect?: boolean;
};

type UseSignalRReturn = {
  connection: SignalR.HubConnection | undefined;
  connectionState: SignalR.HubConnectionState;
  // Bumped on every successful start, so a reconnect is noticed even when React batched it with the drop before it
  connectedTimes: number;

  start: () => Promise<void>;
  stop: SignalR.HubConnection['stop'] | undefined;

  on: SignalR.HubConnection['on'] | undefined;
  off: SignalR.HubConnection['off'] | undefined;
  invoke: SignalR.HubConnection['invoke'] | undefined;
};

export const START_WS_RETRY_DELAY = 5000;

const testWindow = window as Window &
  typeof globalThis & {
    signalRMock?: SignalR.HubConnection;
    wsReady?: boolean;
  };

// This hook is used to create a SignalR connection and manage its lifecycle
// It provides auto / manual connect feature with a retry mechanism that never gives up
// It can be used with a context to keep a unique connection between multiple hook calls
const useSignalR = ({
  url,
  contextConnection,
  connectionName,
  autoConnect = true
}: UseSignalROptions): UseSignalRReturn => {
  if (!url) throw new Error('URL is required');

  // This hook prevents the websocket from being disconnected when the tab is not active
  useAppBrowserLock(`${connectionName.toLowerCase()}_websocket`, true);

  const [connection, setConnection] = React.useState<SignalR.HubConnection | undefined>(contextConnection || undefined);
  React.useEffect(() => {
    if (connection) return;

    const newConnection =
      testWindow.signalRMock ||
      new SignalR.HubConnectionBuilder()
        .withUrl(url, {
          skipNegotiation: true,
          transport: SignalR.HttpTransportType.WebSockets
        })
        .configureLogging(SignalR.LogLevel.Error)
        .build();

    setConnection(newConnection);
  }, [connection, url]);

  const [hasAutoStarted, setHasAutoStarted] = React.useState<boolean>(false);
  const [state, setState] = React.useState<SignalR.HubConnectionState>(
    contextConnection?.state || SignalR.HubConnectionState.Disconnected
  );

  // The pending retry after a failed start, and when the connection went down (to log each outage once)
  const retryTimeout = React.useRef<ReturnType<typeof setTimeout>>();
  const downSince = React.useRef<number>();
  const connectedSince = React.useRef(0);
  // Set on unmount, so a start that fails afterwards doesn't keep an orphaned connection retrying
  const isUnmounted = React.useRef(false);
  const [connectedTimes, setConnectedTimes] = React.useState(0);
  // Read by onclose, registered once per connection
  const startRef = React.useRef<() => Promise<void>>();

  // Register connection lifecycle events. There is no SignalR automatic reconnect: its default policy gives up
  // after 4 tries, so a closed connection is restarted here and retried by startConnection instead
  React.useEffect(() => {
    connection?.onclose((error: Error | undefined): void => {
      // Unmounting stops the connection, which is no outage
      if (isUnmounted.current) return;
      setState(SignalR.HubConnectionState.Disconnected);
      // Restart from here rather than from a render: React can batch this close with the Connected before it,
      // leaving no state change to react to. A hub that accepts and closes right away waits for the next retry,
      // so Cato doesn't reconnect in a tight loop
      const restart = (): void => void startRef.current?.().catch(() => undefined);
      if (Date.now() - connectedSince.current < START_WS_RETRY_DELAY)
        retryTimeout.current = setTimeout(restart, START_WS_RETRY_DELAY);
      else restart();
      if (downSince.current) return;
      downSince.current = Date.now();
      datadogLogs.logger.warn(`Websocket connection closed`, { integration: 'cassie', standalone: true }, error);
    });
  }, [connection]);

  // Start connection handler, retrying every START_WS_RETRY_DELAY until it connects
  const startConnection = React.useCallback(async (): Promise<void> => {
    if (!connection) return Promise.reject(new Error("Can't start connection, connection is not defined"));
    if (connection.state !== SignalR.HubConnectionState.Disconnected)
      return Promise.reject(
        new Error(`Can't start connection, the connection state is not disconnected: ${connection.state}`)
      );

    clearTimeout(retryTimeout.current);
    retryTimeout.current = undefined;

    return connection
      .start()
      .then(() => {
        setState(SignalR.HubConnectionState.Connected);
        setConnectedTimes(times => times + 1);
        connectedSince.current = Date.now();
        if (!downSince.current) return;
        datadogLogs.logger.warn(`Websocket connection reconnected`, {
          integration: 'cassie',
          standalone: true,
          downtimeMs: Date.now() - downSince.current
        });
        downSince.current = undefined;
      })
      .catch((err: Error) => {
        if (isUnmounted.current) return;
        setState(SignalR.HubConnectionState.Disconnected);
        retryTimeout.current = setTimeout(() => void startConnection(), START_WS_RETRY_DELAY);
        if (downSince.current) return;
        downSince.current = Date.now();
        datadogLogs.logger.error(
          `Unable to establish a WS connection with ${url}.`,
          { integration: 'cassie', standalone: true },
          err
        );
      });
  }, [connection, url]);
  startRef.current = startConnection;

  // Retry at once, instead of waiting for the next retry, when the employee comes back or the network does
  // (a background tab too: its timers are throttled)
  React.useEffect(() => {
    const retryNow = (): void => {
      if (retryTimeout.current) void startConnection();
    };
    const retryWhenVisible = (): void => {
      if (!document.hidden) retryNow();
    };
    document.addEventListener('visibilitychange', retryWhenVisible);
    window.addEventListener('focus', retryNow);
    window.addEventListener('online', retryNow);

    return () => {
      document.removeEventListener('visibilitychange', retryWhenVisible);
      window.removeEventListener('focus', retryNow);
      window.removeEventListener('online', retryNow);
    };
  }, [startConnection]);

  // Reset on setup too: StrictMode runs setup, cleanup, setup in development and keeps refs
  React.useEffect(() => {
    isUnmounted.current = false;
    return () => {
      isUnmounted.current = true;
      clearTimeout(retryTimeout.current);
    };
  }, []);

  // Auto-start connection when autoConnect is enabled
  React.useEffect(() => {
    if (connection && autoConnect && !hasAutoStarted && state === SignalR.HubConnectionState.Disconnected) {
      setHasAutoStarted(true);
      void startConnection();
    }
  }, [autoConnect, connection, hasAutoStarted, startConnection, state]);

  return {
    connection,
    connectionState: state,
    connectedTimes,

    start: startConnection,
    stop: connection?.stop.bind(connection),

    on: connection?.on.bind(connection),
    off: connection?.off.bind(connection),
    invoke: connection?.invoke.bind(connection)
  } as const;
};

export default useSignalR;
